interface AutoAcceptInviteParams {
  inviteToken?: string | null;
  inviteValid?: boolean | null;
  inviteStatus?: string | null;
  workspaceId?: string | null;
  invitedEmail?: string | null;
  currentEmail?: string | null;
  currentEmails?: readonly string[] | null;
  isAcceptingInvite?: boolean;
  userChoseCreate?: boolean;
  hasInviteStatusError?: boolean;
}

const normalizeEmail = (email?: string | null) =>
  (email || "").trim().toLowerCase();

export interface InviteIdentityEmail {
  identity_data?: {
    email?: string | null;
  } | null;
}

export const resolveCurrentInviteEmails = (
  userEmail?: string | null,
  profileEmail?: string | null,
  identities?: readonly InviteIdentityEmail[] | null,
): string[] => {
  const candidates = [
    userEmail,
    profileEmail,
    ...(identities || []).map((identity) => identity?.identity_data?.email),
  ]
    .map(normalizeEmail)
    .filter(Boolean);

  return Array.from(new Set(candidates));
};

export const resolveCurrentInviteEmail = (
  userEmail?: string | null,
  profileEmail?: string | null,
) => resolveCurrentInviteEmails(userEmail, profileEmail)[0] || "";

export const inviteEmailMatchesCurrentUser = (
  invitedEmail?: string | null,
  currentEmail?: string | null,
  currentEmails?: readonly string[] | null,
) => {
  const normalizedInvitedEmail = normalizeEmail(invitedEmail);
  if (!normalizedInvitedEmail) {
    return false;
  }

  const normalizedCurrentEmails = [
    ...(currentEmails || []),
    currentEmail,
  ]
    .map(normalizeEmail)
    .filter(Boolean);

  return normalizedCurrentEmails.includes(normalizedInvitedEmail);
};

export const shouldAutoAcceptInviteOnOnboarding = ({
  inviteToken,
  inviteValid,
  inviteStatus,
  invitedEmail,
  currentEmail,
  currentEmails,
  isAcceptingInvite,
  userChoseCreate,
}: AutoAcceptInviteParams): boolean => {
  if (!inviteToken?.trim() || !inviteValid || isAcceptingInvite || userChoseCreate) {
    return false;
  }

  if ((inviteStatus || "").trim().toUpperCase() === "ACCEPTED") {
    return false;
  }

  return inviteEmailMatchesCurrentUser(invitedEmail, currentEmail, currentEmails);
};

export const shouldProcessInviteTokenOnOnboarding = ({
  inviteToken,
  inviteValid,
  inviteStatus,
  invitedEmail,
  currentEmail,
  currentEmails,
  userChoseCreate,
  hasInviteStatusError,
}: AutoAcceptInviteParams): boolean => {
  if (!inviteToken?.trim() || !inviteValid || userChoseCreate || hasInviteStatusError) {
    return false;
  }

  const normalizedStatus = (inviteStatus || "").trim().toUpperCase();
  if (normalizedStatus && normalizedStatus !== "PENDING" && normalizedStatus !== "ACCEPTED") {
    return false;
  }

  return inviteEmailMatchesCurrentUser(invitedEmail, currentEmail, currentEmails);
};

export const shouldEnterAcceptedInviteWorkspaceOnOnboarding = ({
  inviteToken,
  inviteValid,
  inviteStatus,
  workspaceId,
  invitedEmail,
  currentEmail,
  currentEmails,
  userChoseCreate,
}: AutoAcceptInviteParams): boolean => {
  if (!inviteToken?.trim() || !inviteValid || userChoseCreate) {
    return false;
  }

  if ((inviteStatus || "").trim().toUpperCase() !== "ACCEPTED") {
    return false;
  }

  if (!workspaceId?.trim()) {
    return false;
  }

  return inviteEmailMatchesCurrentUser(invitedEmail, currentEmail, currentEmails);
};
